import Cookies from "js-cookie";
import {
    appGetItem,
    appSetItem,
    clearAppSession,
    migrateSessionStorageToLocalStorage,
} from "./appStorage";

const AUTH_COOKIE_OPTIONS = () => ({
    expires: 7,
    secure: typeof window !== "undefined" && window.location.protocol === "https:",
    sameSite: "strict",
    path: "/",
});

export const extractTokenFromResponse = (data = {}, user = {}, row = null) =>
    user?.token ||
    row?.token ||
    data?.token ||
    (Array.isArray(data?.data) ? data.data[0]?.token : data?.data?.token) ||
    "";

export const persistAuthToken = (token) => {
    if (!token) return false;

    Cookies.set("auth_token", token, AUTH_COOKIE_OPTIONS());
    return true;
};

export const updateAuthTokenFromResponse = (data = {}, user = null, row = null) => {
    const resolvedRow =
        row ?? (Array.isArray(data?.data) ? data.data[0] : data?.data);
    const resolvedUser = user ?? resolvedRow?.user ?? resolvedRow ?? data?.user;
    const newToken = extractTokenFromResponse(data, resolvedUser, resolvedRow);
    return persistAuthToken(newToken);
};

export const persistUserSession = (userData = {}, { verificationType } = {}) => {
    if (!userData || typeof userData !== "object") return;

    appSetItem("email", userData.email || "");
    appSetItem("user_email", userData.email || "");
    appSetItem("phone_number", userData.phone_number || "");
    appSetItem("user_phone", userData.phone_number || "");
    appSetItem("user_id", userData.user_id?.toString() || "");
    appSetItem("country_code", userData.country_code || "");
    appSetItem(
        "user_name",
        [userData.first_name, userData.last_name].filter(Boolean).join(" ").trim()
    );
    appSetItem("email_verified", userData.email_verified?.toString() || "0");
    appSetItem("sms_verified", userData.sms_verified?.toString() ?? "");
    appSetItem("payment_status", userData.payment_status?.toString() || "0");
    appSetItem("plan_status", userData.plan_status?.toString() || "");
    appSetItem("plan_type", userData.plan_type || "");
    appSetItem("choose_plan_required", userData.choose_plan_required?.toString() ?? "");
    appSetItem("upgrade_plan_required", userData.upgrade_plan_required?.toString() ?? "");

    if (verificationType != null && verificationType !== "") {
        appSetItem("verification_type", String(verificationType));
    }
};

export const hasUserSessionData = () => {
    try {
        migrateSessionStorageToLocalStorage();
        return Boolean(appGetItem("user_id"));
    } catch {
        return false;
    }
};

export const hasAuthToken = () => Boolean(Cookies.get("auth_token"));

export const hasActiveAuthSession = () => hasAuthToken() && hasUserSessionData();

const isVerifiedFlag = (value) => value === "1" || value === "true";

export const isSmsVerificationPending = () => {
    try {
        const smsVerified = appGetItem("sms_verified");
        if (isVerifiedFlag(smsVerified)) return false;
        if (smsVerified === "0" || smsVerified === "false") return true;

        const verificationType = appGetItem("verification_type");
        return verificationType === "3" && Boolean(appGetItem("phone_number"));
    } catch {
        return false;
    }
};

export const isEmailVerificationPending = () => {
    try {
        const emailVerified = appGetItem("email_verified");
        if (isVerifiedFlag(emailVerified)) return false;

        const verificationType = appGetItem("verification_type");
        return (
            verificationType === "2" &&
            (emailVerified === "0" || emailVerified === "false")
        );
    } catch {
        return false;
    }
};

export const hasPendingVerification = () =>
    isEmailVerificationPending() || isSmsVerificationPending();

export const clearStaleAuthSession = () => {
    Cookies.remove("auth_token", { path: "/" });
    try {
        clearAppSession();
        localStorage.removeItem("pending_invitation_code");
        if (typeof window !== "undefined" && window.sessionStorage) {
            window.sessionStorage.clear();
        }
    } catch {
        // ignore storage errors
    }
};

export const forceAuthLogout = () => {
    clearStaleAuthSession();
};
